<template>
  <TileModule class="summary-module" :items="items" data-cy="summary-module">
    <template v-slot="props">
      <div class="summary-module-item" data-cy="summary-module-item">
        <div class="summary-module-item__header">
          <font-awesome-icon
            class="summary-module__icon"
            :icon="props.item.icon"
          /><span class="summary-module__num" data-cy="summary-module-num">{{
            props.item.num
          }}</span>
          <Skeleton
            v-if="props.item.num == ''"
            :shape="'text'"
            :style="{ width: '40%', height: '36px' }"
            :animation="{ type: 'wave' }"
          />
        </div>
        <span class="summary-module__text" data-cy="summary-module-text">{{
          props.item.text
        }}</span>
      </div>
    </template>
  </TileModule>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import TileModule from "./TileModule.vue";
import { useMainStore } from "@/stores/useMainStore";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { addCommas } from "@/shared/helpers/formatHelper";
import { Skeleton } from "@progress/kendo-vue-indicators";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";

interface SummaryItem {
  icon: string;
  num: string;
  text: string;
  getNumAsync: () => Promise<string>;
}

const main = useMainStore();

const items = ref<SummaryItem[]>([]);

const displayCountParams = computed(() => ({
  userLogin: main.userLogin,
  companyLogin: main.companyLogin,
}));

const getProductDisplayCountAsync = async () => {
  let count = 0;
  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(displayCountParams.value, "GetProductDisplayCount"),
      () => api.get("GetProductDisplayCount", displayCountParams.value)
    ),
    (data) => {
      count = data;
    },
    "SummaryModule.vue",
    "getProductDisplayCountAsync"
  );
  return addCommas(count.toString());
};

const getIngredientDisplayCountAsync = async () => {
  let count = 0;
  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(displayCountParams.value, "GetIngredientDisplayCount"),
      () => api.get("GetIngredientDisplayCount", displayCountParams.value)
    ),
    (data) => {
      count = data;
    },
    "SummaryModule.vue",
    "getIngredientDisplayCountAsync"
  );
  return addCommas(count.toString());
};

const getProductDisplayScheduleBCountAsync = async () => {
  let count = 0;
  await handleResponsePromiseAsync(
    CacheService.getResponseAsync(
      createCacheKey(
        displayCountParams.value,
        "GetProductDisplayScheduleBCount"
      ),
      () => api.get("GetProductDisplayScheduleBCount", displayCountParams.value)
    ),
    (data) => {
      count = data;
    },
    "SummaryModule.vue",
    "getProductDisplayCountAsync"
  );
  return addCommas(count.toString());
};

items.value = [
  {
    icon: "fa fa-users",
    num: "",
    text: "OU Certified Products",
    getNumAsync: getProductDisplayCountAsync,
  },
  {
    icon: "fa fa-cubes",
    num: "",
    text: "My Products",
    getNumAsync: getProductDisplayScheduleBCountAsync,
  },
  {
    icon: "fa fa-database",
    num: "",
    text: "My Ingredients",
    getNumAsync: getIngredientDisplayCountAsync,
  },
];

const getAllNumsAsync = async () => {
  const promises = [] as Promise<void>[];
  items.value.forEach((item) => {
    promises.push(
      (async () => {
        item.num = await item.getNumAsync();
      })()
    );
  });
  await Promise.all(promises);
};

onBeforeMount(async () => {
  await getAllNumsAsync();
});
</script>

<style scoped lang="scss">
.summary-module {
  &__icon {
    margin-bottom: 2px;
    color: #333;
    font-size: 16px;
  }

  &-item {
    &__header {
      display: flex;
      gap: 8px;
      justify-content: center;
      align-items: center;
      flex-wrap: wrap;
    }
  }

  &__num {
    font-size: 16px;
    font-weight: 400;
    color: #222;

    @include media-breakpoint-up(sm) {
      font-size: 24px;
    }
  }

  &__text {
    color: #666;
    line-height: 1.6;
  }
}
</style>
